import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/FadeIn';
import { ArrowLeftIcon, ArrowRightIcon, SearchIcon, FlaskConicalIcon, TrendingUpIcon, LandmarkIcon, BuildingIcon, WalletIcon, HeartPulseIcon, ShieldCheckIcon, GitBranchIcon, ServerCogIcon, ScaleIcon } from 'lucide-react';
const methodPillars = [{
  title: 'Spec-driven development',
  description: 'Work starts from precise, machine-readable intent so AI produces reviewable output instead of guesswork.',
  icon: GitBranchIcon
}, {
  title: 'Purposeful MCP orchestration',
  description: 'Model Context Protocol servers wired deliberately into your delivery toolchain, not bolted on per developer.',
  icon: ServerCogIcon
}, {
  title: 'AI-assisted architecture governance',
  description: 'Architecture review moves upstream, with AI enforcing patterns rather than reviewers catching drift late.',
  icon: ScaleIcon
}, {
  title: 'Secure-by-design pipelines',
  description: 'Threat modeling and regulatory requirements are engineered into the workflow from the first commit.',
  icon: ShieldCheckIcon
}];
const engagementPhases = [{
  phase: 'Assessment',
  icon: SearchIcon,
  description: 'A focused review of your current SDLC, tooling, security posture, and team readiness, producing an AI-DLC roadmap with prioritized opportunities and projected impact.'
}, {
  phase: 'Pilot',
  icon: FlaskConicalIcon,
  description: 'AI-DLC applied to a live project line within your organization, with MCP infrastructure, governance patterns, and threat-modeling practices established. Measured before-and-after results.'
}, {
  phase: 'Scale',
  icon: TrendingUpIcon,
  description: 'Rollout across teams, supported by playbooks, training, architecture review board integration, and leadership coaching, so the capability lives inside your organization rather than depending on ours.'
}];
const audiences = [{
  label: 'Banking',
  icon: LandmarkIcon
}, {
  label: 'Credit Unions',
  icon: BuildingIcon
}, {
  label: 'Fintech',
  icon: WalletIcon
}, {
  label: 'Healthtech',
  icon: HeartPulseIcon
}];
export function AiNativeEngineering() {
  return <>
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden" data-id="element-716" data-mp="3cmieg8bv2wv5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 z-0" data-id="element-717" data-mp="3ckp7st190cn5"></div>
        <div className="absolute inset-0 bg-radial-glow z-0" data-id="element-718" data-mp="37p9uq3b2ekrs"></div>
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-blue/5 rounded-full blur-3xl animate-pulse-slow" data-id="element-719" data-mp="3mcuxfw412hz6"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-720" data-mp="3hl345dthly5c">
          <FadeIn data-id="element-721" data-mp="2tw0qeh1z4pg4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-teal transition-colors mb-10" data-id="element-722" data-mp="2tvik0rllkuuh">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-723" data-mp="14en1t2wl6r5x" />
              Back to overview
            </Link>
          </FadeIn>

          <div className="max-w-4xl" data-id="element-724" data-mp="196wxkzkdulg4">
            <FadeIn delay={0.05} data-id="element-725" data-mp="197y9f6983byw">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 border border-teal/20 text-teal text-sm font-medium mb-8" data-id="element-726" data-mp="veqf36trc2di">
                <ShieldCheckIcon className="w-4 h-4" data-id="element-727" data-mp="zgbpmlitk4kj" />
                <span data-id="element-728" data-mp="1n5ehsnb2xfta">AI-DLC Methodology</span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-729" data-mp="1s4hftnsn6lrr">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.05]" data-id="element-730" data-mp="1s3g21bk8rl6c">
                AI-Native Engineering{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal to-blue" data-id="element-731" data-mp="1j3t10qvfchdt">
                  Transformation
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-732" data-mp="7omhbg392wuf">
              <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-3xl leading-relaxed" data-id="element-733" data-mp="7pnr7h8j5dak">
                Faster delivery. Stronger security. Built for regulated
                environments.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-734" data-mp="i5q1kaa8osdq">
              <a href="https://calendly.com/arthur-w-miller/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-400 text-navy font-semibold px-8 py-4 rounded-md transition-colors duration-200 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] text-lg" data-id="element-735" data-mp="d87y2zezr5d4">
                Schedule a Conversation
                <ArrowRightIcon className="w-5 h-5" data-id="element-736" data-mp="wv5nncyts9n3" />
              </a>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* The problem / the approach */}
      <section className="py-24 bg-slate border-y border-white/5" data-id="element-737" data-mp="3hnzbd0nxvfcs">
        <div className="max-w-7xl mx-auto px-6" data-id="element-738" data-mp="3co4mf2nq4zud">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20" data-id="element-739" data-mp="3cma0bl0pv1op">
            <FadeIn data-id="element-740" data-mp="1s6vfviyw84ci">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-widest mb-6" data-id="element-741" data-mp="1s7dm98f9ryy5">
                The gap
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-snug" data-id="element-742" data-mp="216iho5vjbc63">
                Most organizations adopted AI tools. Few changed how work
                actually flows.
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed" data-id="element-743" data-mp="2awtlztj4rndi">
                The result is faster typing and the same bottlenecks — while
                security, compliance, and architecture governance remain
                outside the AI conversation entirely.
              </p>
            </FadeIn>

            <FadeIn direction="left" delay={0.1} data-id="element-744" data-mp="2owcsyj07gso8">
              <p className="text-sm font-medium text-teal uppercase tracking-widest mb-6" data-id="element-745" data-mp="2otyz2dh9hmi9">
                Our approach
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-snug" data-id="element-746" data-mp="uhik8oaeh01s">
                We restructure the entire delivery process around AI.
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed" data-id="element-747" data-mp="1stuy0vsciwcs">
                Our AI Development Lifecycle (AI-DLC) methodology treats
                security and regulatory requirements as engineering inputs from
                the start, not obligations bolted on afterward.
              </p>
            </FadeIn>
          </div>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-16" data-id="element-748" data-mp="2gqs9qnlzf1zz">
            {methodPillars.map(pillar => {
            const Icon = pillar.icon;
            return <StaggerItem key={pillar.title} className="h-full" data-id="element-749" data-mp="2qgbujyt995xh">
                  <div className="bg-navy border border-white/10 rounded-2xl p-8 h-full flex flex-col" data-id="element-750" data-mp="2li0kpo2ymvf9">
                    <div className="mb-6 inline-flex self-start p-3 rounded-xl bg-slate border border-white/5 text-teal" data-id="element-751" data-mp="2zhjqwbc7nwox">
                      <Icon className="w-6 h-6" strokeWidth={1.5} data-id="element-752" data-mp="2ukjgjo8bw7s0" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3" data-id="element-753" data-mp="3e6bn1g24f190">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed text-[15px]" data-id="element-754" data-mp="3j20mzki1yk5u">
                      {pillar.description}
                    </p>
                  </div>
                </StaggerItem>;
          })}
          </StaggerContainer>
        </div>
      </section>

      {/* Case study proof */}
      <section className="py-24 bg-navy relative overflow-hidden" data-id="element-755" data-mp="3058xgtbx38tc">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-teal/5 rounded-full blur-[130px] pointer-events-none" data-id="element-756" data-mp="306k18t5d4gkg"></div>

        <div className="max-w-5xl mx-auto px-6 relative z-10" data-id="element-757" data-mp="2qghbd1tjfiqc">
          <FadeIn data-id="element-758" data-mp="2cfoc63sajzgw">
            <div className="bg-slate border border-teal/20 rounded-3xl p-8 md:p-14 shadow-[0_0_60px_-20px_rgba(0,212,170,0.2)]" data-id="element-759" data-mp="2cf4y4y5rhv85">
              <p className="text-sm font-medium text-teal uppercase tracking-widest mb-8" data-id="element-760" data-mp="2hdg8d9zyy7qw">
                Case study
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-start" data-id="element-761" data-mp="2wueqwst2bv5a">
                <div data-id="element-762" data-mp="2myl66m56wvcf">
                  <p className="text-6xl md:text-7xl font-bold text-white leading-none mb-3" data-id="element-763" data-mp="2my1xzx5cdlbl">
                    97%
                  </p>
                  <p className="text-teal font-semibold" data-id="element-764" data-mp="2iwgdpqqha4rd">
                    Reduction in delivery time
                  </p>
                </div>

                <div data-id="element-765" data-mp="297gce0oi6xa0">
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-5 leading-snug" data-id="element-766" data-mp="296wxyty2ar0q">
                    Global health-and-wellness e-commerce enterprise
                  </h2>
                  <p className="text-lg text-gray-400 leading-relaxed mb-6" data-id="element-767" data-mp="1z9ipbgidr6iz">
                    AI-DLC was implemented across the delivery organization,
                    restructuring how specifications, architecture review, and
                    security validation flowed through the lifecycle. Alongside
                    the reduction in delivery time, the rate of successfully
                    delivered projects increased sharply.
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-768" data-mp="ar7g160fbgue">
                    One of four organizations where the methodology has been
                    implemented end to end.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* How we engage */}
      <section className="py-24 bg-slate border-y border-white/5" data-id="element-769" data-mp="3n601h5ghkp2w">
        <div className="max-w-7xl mx-auto px-6" data-id="element-770" data-mp="3n4fa1opgxm99">
          <FadeIn className="max-w-3xl mb-16" data-id="element-771" data-mp="3s52vluuxhaw3">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-772" data-mp="3s41mhvxh2yh0">
              How We Engage
            </h2>
            <p className="text-xl text-gray-400" data-id="element-773" data-mp="2zhql4j21ktj5">
              Three phases, each producing evidence before the next begins.
            </p>
          </FadeIn>

          <StaggerContainer className="space-y-6" data-id="element-774" data-mp="34e78hb1f33v6">
            {engagementPhases.map((phase, index) => {
            const Icon = phase.icon;
            return <StaggerItem key={phase.phase} data-id="element-775" data-mp="3sp5zr26z877t" data-mpi={index}>
                  <div className="bg-navy border border-white/10 rounded-2xl p-8 md:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start" data-id="element-776" data-mp="1k68pnm09ag1">
                    <div className="flex items-center gap-5" data-id="element-777" data-mp="6ir0qzr3snte">
                      <span className="text-4xl font-bold text-white/15 tabular-nums" data-id="element-778" data-mp="351o42vklcozk">
                        0{index + 1}
                      </span>
                      <div className="p-3 rounded-xl bg-slate border border-white/5 text-teal" data-id="element-779" data-mp="39xwrsxcutqic">
                        <Icon className="w-6 h-6" strokeWidth={1.5} data-id="element-780" data-mp="3ewzkdibne49f" />
                      </div>
                    </div>

                    <div data-id="element-781" data-mp="2maetsk2zxw6x">
                      <h3 className="text-2xl font-semibold text-white mb-3" data-id="element-782" data-mp="2mb6oztsolrwo">
                        {phase.phase}
                      </h3>
                      <p className="text-gray-400 text-lg leading-relaxed max-w-3xl" data-id="element-783" data-mp="2r7nbkjk8fy7n">
                        {phase.description}
                      </p>
                    </div>
                  </div>
                </StaggerItem>;
          })}
          </StaggerContainer>
        </div>
      </section>

      {/* Who it's for + Why FiQIS */}
      <section className="py-24 bg-navy" data-id="element-784" data-mp="24thnpvdbqfx2">
        <div className="max-w-7xl mx-auto px-6" data-id="element-785" data-mp="24ussnyii9tpr">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20" data-id="element-786" data-mp="20rcwt4e9fxe4">
            <FadeIn data-id="element-787" data-mp="1w0o6hsebh6gk">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-788" data-mp="1w04m851725zd">
                Who It's For
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed mb-10" data-id="element-789" data-mp="1r1tdjxmmy1iq">
                Engineering organizations that need measurable gains in
                delivery speed without compromising compliance, auditability,
                or security.
              </p>

              <StaggerContainer className="grid grid-cols-2 gap-4" data-id="element-790" data-mp="1d1a9t2p7dhmd">
                {audiences.map(audience => {
                const Icon = audience.icon;
                return <StaggerItem key={audience.label} className="flex items-center gap-3 bg-slate/50 border border-white/5 rounded-xl px-5 py-4" data-id="element-791" data-mp="17g1bqj3sfw82">
                      <Icon className="w-5 h-5 text-teal flex-shrink-0" strokeWidth={1.5} data-id="element-792" data-mp="yfv072rnh6bj" />
                      <span className="text-gray-300 font-medium" data-id="element-793" data-mp="1q5zrzmhqovms">
                        {audience.label}
                      </span>
                    </StaggerItem>;
              })}
              </StaggerContainer>
            </FadeIn>

            <FadeIn direction="left" delay={0.1} data-id="element-794" data-mp="1hc4lob4vsz5k">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-795" data-mp="253jridvv9ylk">
                Why FiQIS
              </h2>
              <div className="space-y-5 text-lg text-gray-400 leading-relaxed" data-id="element-796" data-mp="29u8no6ihrjr1">
                <p data-id="element-797" data-mp="2j8ackhpbe236">
                  FiQIS was founded by the enterprise architect behind Zelle®
                  and Real-Time Payments — infrastructure integrating more than
                  2,000 financial institutions — and the co-founder and CTO of
                  a regulated BNPL platform led through a successful exit.
                </p>
                <p data-id="element-798" data-mp="2o0ahypo5ln75">
                  Our approach to AI-native engineering is grounded in
                  national-scale systems, defense-grade security, and the
                  practical realities of FDIC, NCUA, PCI-DSS, and SOC 2
                  environments.
                </p>
              </div>

              <div className="mt-8 bg-slate border border-white/10 rounded-xl p-6" data-id="element-799" data-mp="2a614rzlfkmth">
                <p className="text-sm text-gray-500 mb-2" data-id="element-800" data-mp="257ptdkfhm495">
                  Methodology documented in
                </p>
                <p className="text-white font-semibold" data-id="element-801" data-mp="3lp3vo3mzy33u">
                  AI-SDLC and the New Enterprise Architecture Approach
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 relative overflow-hidden" data-id="element-802" data-mp="1v6yk2tt39rkw">
        <div className="absolute inset-0 bg-gradient-to-b from-navy to-slate z-0" data-id="element-803" data-mp="205i8r1z2hhgy"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20 z-0" data-id="element-804" data-mp="246velc25tknv"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-805" data-mp="244r0606mx39c">
          <FadeIn data-id="element-806" data-mp="295f2vj4878je">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6" data-id="element-807" data-mp="293u8bthwvplj">
              Ready to Move Beyond AI Experimentation?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-808" data-mp="2ivht5bjyq8e1">
              Start with an assessment. You'll leave with an AI-DLC roadmap,
              prioritized opportunities, and projected impact for your delivery
              organization.
            </p>
            <a href="https://calendly.com/arthur-w-miller/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-teal hover:bg-teal-400 text-navy font-bold px-8 py-4 rounded-md transition-colors duration-200 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)] text-lg" data-id="element-809" data-mp="2wvivywjsfpw5">
              Schedule a Conversation
              <ArrowRightIcon className="w-5 h-5" data-id="element-810" data-mp="7966vcsx9fdk" />
            </a>
          </FadeIn>
        </div>
      </section>
    </>;
}